import { useState, useEffect } from 'react';
import { adminAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Loader2, Shield, Zap, Clock, Users, CheckCircle, XCircle, AlertTriangle, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPanelPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await adminAPI.status();
      setData(res.data);
    } catch (e) { toast.error('Admin verisi alinamadi'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Veri yok</div>;

  const healthColor = data.deliverability?.status === 'healthy' ? 'text-green-500' : data.deliverability?.status === 'warning' ? 'text-yellow-500' : 'text-red-500';

  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">Admin Panel</h1>
        <Button variant="outline" size="sm" onClick={() => { setLoading(true); load(); }}><RefreshCw className="w-4 h-4 mr-1" /> Yenile</Button>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-lg bg-card p-4 text-center">
          <Shield className={`w-5 h-5 mx-auto mb-1 ${healthColor}`} />
          <p className="text-2xl font-bold">{data.deliverability?.score || 0}</p>
          <p className="text-xs text-muted-foreground">Health Score</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Users className="w-5 h-5 mx-auto text-blue-500 mb-1" />
          <p className="text-2xl font-bold">{data.campaigns?.total || 0}</p>
          <p className="text-xs text-muted-foreground">Kampanyalar</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <Zap className="w-5 h-5 mx-auto text-amber-500 mb-1" />
          <p className="text-2xl font-bold">{data.warmup?.day || 1}</p>
          <p className="text-xs text-muted-foreground">Warmup Day</p>
        </div>
        <div className="rounded-lg bg-card p-4 text-center">
          <XCircle className="w-5 h-5 mx-auto text-red-400 mb-1" />
          <p className="text-2xl font-bold">{data.suppression?.total || 0}</p>
          <p className="text-xs text-muted-foreground">Suppressed</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Queue Status */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Queue Durumu</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {Object.entries(data.queue || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm">
                  <span className="text-muted-foreground capitalize">{k}</span>
                  <span className="font-mono">{v}</span>
                </div>
              ))}
            </div>
            {data.workers?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-1">Aktif Workers:</p>
                <div className="flex gap-1">{data.workers.map((w, i) => <Badge key={i} variant="outline" className="text-[10px]">{w}</Badge>)}</div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Provider Health */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Provider Health</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.providers || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="font-medium capitalize">{p.provider}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Open %{p.open_rate}</span>
                    <Badge variant={p.status === 'healthy' ? 'default' : p.status === 'warning' ? 'secondary' : 'destructive'} className="text-[10px]">
                      {p.health_score}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Warmup */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Domain Warmup</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Asama</span><span>{data.warmup?.stage}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Bugun Limit</span><span>{data.warmup?.max_today}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Gonderildi</span><span>{data.warmup?.sent_today}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Kalan</span><span className="text-green-500">{data.warmup?.remaining}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Suppression */}
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Suppression List</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Toplam</span><span>{data.suppression?.total || 0}</span></div>
              {Object.entries(data.suppression?.by_reason || {}).map(([r, c]) => (
                <div key={r} className="flex justify-between"><span className="text-muted-foreground capitalize">{r}</span><span>{c}</span></div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Events */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Son Webhook Eventleri</CardTitle></CardHeader>
        <CardContent>
          {data.recent_webhook_events?.length > 0 ? (
            <div className="space-y-1">
              {data.recent_webhook_events.map((ev, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/30 text-sm">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{ev.event}</Badge>
                    <span className="text-muted-foreground">{ev.email}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{ev.timestamp ? new Date(ev.timestamp).toLocaleString('tr-TR') : ''}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">Henuz webhook eventi yok</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
